import { ReactElement, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Survey from '../components/survey/Survey'
import apiServices from '../libs/services'
import { LoadingSpinner } from '../components/atoms'
import { useAuth } from '../hooks/useAuth'
import { MobileSurveyCompletionMessage, useMobileSurveyCompletion } from '../components/survey/MobileSurveyCompletion'

export const POST_90_SURVEY_ID_SLUG = '90POST_SURVEY'

function Post90SurveyPage (): ReactElement {
  const navigate = useNavigate()
  const { userProfile, refreshUserProfile } = useAuth()
  const [surveyId, setSurveyId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { showMobileCompletion, gateMobileCompletion } = useMobileSurveyCompletion()

  useEffect(() => {
    if (showMobileCompletion) return
    const fetchSurveyMetadata = async (): Promise<void> => {
      try {
        const res = await apiServices.getSurveyByUserGeneratedId(POST_90_SURVEY_ID_SLUG)
        const id = res.data?.id as string | undefined

        if (id === undefined || id === null) {
          setError('Survey ID not found in response.')
          return
        }

        const completedIds = userProfile?.user_profile?.completed_survey_ids ?? []
        const sentEmailIds = userProfile?.user_profile?.survey_ids_emailed ?? []

        // Already completed — redirect to dashboard
        if (completedIds.includes(id)) {
          navigate('/user-dashboard', { replace: true })
          return
        }

        // Not sent the email — user shouldn't be here
        if (!sentEmailIds.includes(id)) {
          navigate('/user-dashboard', { replace: true })
          return
        }

        setSurveyId(id)
      } catch (err: any) {
        console.error('Failed to fetch survey metadata.', err)
        setError('Failed to load survey metadata.')
      } finally {
        setLoading(false)
      }
    }

    void fetchSurveyMetadata()
  }, [navigate, userProfile, showMobileCompletion])

  const handleFinish = async (): Promise<void> => {
    // On a phone, show the completion message instead of navigating into the portal.
    if (gateMobileCompletion()) return
    await refreshUserProfile()
    navigate('/user-dashboard', { replace: true })
  }

  if (showMobileCompletion) {
    return <MobileSurveyCompletionMessage />
  }

  if (loading) {
    return (
      <div className='bg-dopage-black min-h-screen flex items-center justify-center'>
        <LoadingSpinner className='w-[25vmin] min-w-[80px] max-w-[240px]' />
      </div>
    )
  }

  if (error !== null || surveyId === null) {
    return (
      <div className='bg-dopage-black min-h-screen flex items-center justify-center text-red-500 font-primary'>
        {error ?? 'An unexpected error occurred.'}
      </div>
    )
  }

  return <Survey surveyId={surveyId} onFinish={handleFinish} />
}

export default Post90SurveyPage
